import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ShieldCheck, AlertCircle, Lock, CheckCircle2, FileImage, Send } from "lucide-react";
import axios from "axios";

import { API } from "../api";
import {
  FileUploadWidget,
  StakeholderConfirmationCard,
} from "../components/ChatWidgets";

const PHASES = ["preview", "otp", "chat"];

const inviteClient = axios.create({ baseURL: API });

function cleanMessage(text) {
  return (text || "").replace(/```json\s*\{[\s\S]*?\}\s*```/g, "").trim();
}

export default function InvitePage() {
  const { token } = useParams();
  const [phase, setPhase] = useState("preview");
  const [preview, setPreview] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [resent, setResent] = useState(false);

  // Chat state
  const [access, setAccess] = useState(null);
  const [me, setMe] = useState(null);
  const [messages, setMessages] = useState([]);
  const [widget, setWidget] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const scrollRef = useRef(null);

  // Load preview
  useEffect(() => {
    inviteClient.get(`/invitations/${token}/preview`)
      .then((r) => setPreview(r.data))
      .catch(() => setError("Could not load invitation"));
  }, [token]);

  // Auto-send the OTP when preview shows PENDING
  async function startOtp() {
    setBusy(true); setError(null);
    try {
      await inviteClient.post(`/invitations/${token}/send-otp`);
      setPhase("otp");
      setResent(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not send code");
    } finally { setBusy(false); }
  }

  async function verifyOtp() {
    setBusy(true); setError(null);
    try {
      const r = await inviteClient.post(`/invitations/${token}/verify-otp`, { otp });
      const tk = r.data.access_token;
      setAccess(tk);
      inviteClient.defaults.headers.Authorization = `Bearer ${tk}`;
      const meResp = await inviteClient.get(`/invitee/me`);
      setMe(meResp.data);
      const hist = await inviteClient.get(`/invitee/chat-history`);
      const visible = hist.data.map((m, i) => ({
        id: `h-${i}`, role: m.role, text: m.role === "assistant" ? cleanMessage(m.text) : m.text,
      })).filter((m) => m.text);
      setMessages(visible);
      setPhase("chat");
      // Greet via synthetic turn if no history
      if (visible.length === 0) {
        runTurn("Hi Infi — I just verified my email. What do you need from me?", true);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "locked") {
        setError("Too many incorrect codes. This invitation is locked for 1 hour for safety.");
      } else if (detail === "incorrect code") {
        setError("That code didn't match. Please double-check the most recent email.");
      } else if (detail === "invitation revoked") {
        setError("This invitation has been revoked. Please ask your co-founder to send a new one.");
      } else if (detail === "invitation expired") {
        setError("This invitation has expired. Please ask your co-founder to send a new one.");
      } else {
        setError(detail || "Could not verify code");
      }
    } finally { setBusy(false); }
  }

  async function runTurn(text, synthetic = false) {
    setError(null); setWidget(null); setSending(true);
    setLoadingMsg("Infi is thinking…");
    if (!synthetic) {
      setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", text }]);
    }
    try {
      const r = await inviteClient.post("/invitee/chat", { user_message: text });
      const data = r.data;
      const calls = data.tool_calls_made || [];
      if (calls.some((t) => t.name === "extract_document")) {
        setLoadingMsg("Reading your document…");
      }
      const failed = calls.find((t) => !t.ok);
      if (failed) setError(`I couldn't complete a step (${failed.name}). ${failed.error || ""}`);
      setMe(data.me);
      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant",
                                     text: cleanMessage(data.assistant_message) }]);
      setWidget(data.widget || null);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Something went wrong";
      if (e?.response?.status === 403) {
        toast.error("Session expired or revoked");
        setPhase("preview");
        setAccess(null);
      } else {
        setError(detail);
      }
    } finally { setSending(false); setLoadingMsg(""); }
  }

  async function handleUpload(file, target) {
    setLoadingMsg("Uploading…");
    setSending(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await inviteClient.post("/invitee/files", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      const fid = r.data.file_id;
      let msg;
      if (target === "stakeholder:pan") msg = `I just uploaded my PAN card. file_id=${fid}. Please extract_document(file_id='${fid}', expected_type='PAN').`;
      else if (target === "stakeholder:aadhaar") msg = `I just uploaded my Aadhaar card. file_id=${fid}. Please extract_document(file_id='${fid}', expected_type='AADHAAR').`;
      else if (target === "stakeholder:photo") msg = `I uploaded my photo. file_id=${fid}.`;
      else msg = `I uploaded a file. file_id=${fid}`;
      await runTurn(msg);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setSending(false); setLoadingMsg("");
    }
  }

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loadingMsg]);

  // ---------- PREVIEW ----------
  if (phase === "preview") {
    if (!preview && !error) {
      return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading invitation…</div>;
    }
    if (preview && (preview.status === "EXPIRED" || preview.status === "NOT_FOUND" || preview.status === "REVOKED")) {
      return (
        <ExpiredOrRevoked status={preview.status} />
      );
    }
    return (
      <div className="min-h-screen bg-[var(--canvas)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <div className="w-12 h-12 rounded-xl bg-[#C5283F] text-white flex items-center justify-center mb-4">
            <Mail size={20} />
          </div>
          <h1 className="font-display text-2xl font-semibold mb-1">You&apos;ve been invited</h1>
          <p className="text-sm text-slate-500 mb-5">
            {preview?.company_name
              ? <>To add yourself as <b>{(preview.role || "").replace(/_/g, " ")}</b> for <b>{preview.company_name}</b>.</>
              : <>To add yourself to your co-founder&apos;s company incorporation.</>}
          </p>
          <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm mb-5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Verification email</div>
            <div className="font-medium text-slate-900">{preview?.invitee_email_masked || ""}</div>
          </div>
          <button onClick={startOtp} disabled={busy}
                  data-testid="send-otp-btn"
                  className="w-full bg-[#C5283F] text-white rounded-lg py-3 font-medium hover:bg-[#A82035] disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <ShieldCheck size={16} /> {busy ? "Sending…" : "Send me a code"}
          </button>
          {error && (
            <div className="text-sm text-red-800 mt-3 inline-flex items-start gap-1.5">
              <AlertCircle size={14} className="mt-0.5" /> {error}
            </div>
          )}
          <div className="text-[11px] text-slate-400 mt-6">
            We&apos;ll send a 6-digit code to your inbox. The link expires {preview?.expires_at && (<>at {new Date(preview.expires_at).toLocaleString()}</>)}.
          </div>
        </div>
      </div>
    );
  }

  // ---------- OTP ----------
  if (phase === "otp") {
    return (
      <div className="min-h-screen bg-[var(--canvas)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <div className="w-12 h-12 rounded-xl bg-[#C5283F] text-white flex items-center justify-center mb-4">
            <Lock size={20} />
          </div>
          <h1 className="font-display text-2xl font-semibold mb-1">Enter your code</h1>
          <p className="text-sm text-slate-500 mb-5">
            We sent a 6-digit code to <b>{preview?.invitee_email_masked}</b>. The code expires in 10 minutes.
          </p>
          <input
            type="text" inputMode="numeric" maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            data-testid="otp-input"
            placeholder="••••••"
            className="w-full text-center text-3xl tracking-[0.4em] font-mono font-bold bg-slate-50 border border-slate-300 rounded-lg p-4 outline-none focus:border-[#C5283F]"
          />
          <button onClick={verifyOtp} disabled={busy || otp.length !== 6}
                  data-testid="otp-verify-btn"
                  className="w-full mt-4 bg-[#C5283F] text-white rounded-lg py-3 font-medium hover:bg-[#A82035] disabled:opacity-50">
            {busy ? "Verifying…" : "Verify"}
          </button>
          <button onClick={startOtp} disabled={busy}
                  data-testid="otp-resend-btn"
                  className="w-full mt-2 text-sm text-slate-600 hover:text-slate-900">
            {resent ? "Send a new code" : "Resend code"}
          </button>
          {error && (
            <div className="text-sm text-red-800 mt-3 inline-flex items-start gap-1.5" data-testid="otp-error">
              <AlertCircle size={14} className="mt-0.5" /> {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- CHAT ----------
  return (
    <div className="h-screen bg-[var(--canvas)] flex flex-col md:flex-row overflow-hidden">
      <div className="flex-1 md:w-3/5 md:border-r md:border-slate-200 flex flex-col min-h-0">
        <div className="border-b border-slate-200 bg-white px-4 sm:px-6 py-3 flex items-center justify-between">
          <div>
            <div className="font-display text-lg font-semibold">Infi · Co-founder onboarding</div>
            <div className="text-xs text-slate-500">You&apos;re confirming your own details only.</div>
          </div>
          <div className="text-xs text-slate-500">{me?.invitee_email}</div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll px-4 sm:px-6 py-6 space-y-3"
             data-testid="invitee-chat-scroll">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} infin-fade-up`}>
              <div
                data-testid={`invitee-msg-${m.role}`}
                className={[
                  "max-w-[88%] sm:max-w-[80%] px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap",
                  m.role === "user"
                    ? "bg-[#C5283F] text-white rounded-2xl rounded-tr-sm shadow-sm"
                    : "bg-white text-slate-900 border border-slate-200 rounded-2xl rounded-tl-sm shadow-sm",
                ].join(" ")}>
                {m.text}
              </div>
            </div>
          ))}
          {widget && messages.length > 0 && messages[messages.length - 1].role === "assistant" && (
            <div className="flex justify-start">
              <div className="max-w-[88%] sm:max-w-[80%] w-full">
                {widget.widget === "file_upload" && (
                  <FileUploadWidget accept={widget.accept} target={widget.target} onUpload={handleUpload} busy={sending} />
                )}
                {widget.widget === "confirmation_card" && me && (
                  <StakeholderConfirmationCard
                    stakeholder={{
                      stakeholder_id: me.stakeholder_id, role: me.role, share_percent: me.share_percent,
                      personal: me.personal, identity: me.identity, contact: me.contact,
                      data_provenance: me.data_provenance,
                    }}
                    onConfirm={(form) => {
                      setWidget(null);
                      runTurn(`I've reviewed my details. Please upsert_stakeholder with these corrected fields and set user_confirmed=true. Corrections: ${JSON.stringify(form)}`);
                    }}
                    onCancel={() => setWidget(null)}
                  />
                )}
              </div>
            </div>
          )}

          {sending && (
            <div className="flex justify-start infin-fade-up">
              <div className="bg-white border border-slate-200 px-4 py-2.5 rounded-2xl rounded-tl-sm text-sm text-slate-500 inline-flex items-center gap-2">
                <span className="infin-typing-dot" />
                <span className="infin-typing-dot" />
                <span className="infin-typing-dot" />
                <span>{loadingMsg || "Infi is thinking…"}</span>
              </div>
            </div>
          )}
          {error && (
            <div className="flex justify-start">
              <div className="bg-red-50 text-red-800 border border-red-200 px-4 py-2.5 rounded-lg text-sm flex items-start gap-2 max-w-[88%]"
                   data-testid="invitee-chat-error">
                <AlertCircle size={14} className="mt-0.5 shrink-0" /> {error}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white px-4 sm:px-6 py-3">
          <form onSubmit={(e) => { e.preventDefault(); const v = input.trim(); if (!v) return; setInput(""); runTurn(v); }}
                className="flex items-center gap-2">
            <input
              data-testid="invitee-chat-input" value={input}
              onChange={(e) => setInput(e.target.value)} disabled={sending}
              placeholder="Type your message…"
              className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C5283F] focus:border-transparent disabled:opacity-50"
            />
            <button type="submit" disabled={sending || !input.trim()}
                    data-testid="invitee-chat-send-btn"
                    className="bg-[#C5283F] text-white px-4 py-3 rounded-lg hover:bg-[#A82035] disabled:opacity-50 inline-flex items-center gap-1.5 text-sm font-medium">
              <Send size={14} /> Send
            </button>
          </form>
          <div className="text-[11px] text-slate-400 mt-2 hidden sm:flex items-center gap-2">
            <FileImage size={11} /> Infi will only see your details, never the rest of the case.
          </div>
        </div>
      </div>

      {/* Own-stakeholder summary card (desktop) */}
      <div className="hidden md:block md:w-2/5 bg-white overflow-y-auto p-8">
        <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500 mb-3">
          Your stakeholder slot
        </div>
        {me && <MyCard me={me} />}
      </div>
    </div>
  );
}

function MyCard({ me }) {
  const name = [me.personal?.first_name, me.personal?.last_name].filter(Boolean).join(" ") || "—";
  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-display font-semibold">Your details</div>
          {me.user_confirmed && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-2 py-1 rounded-full">
              <CheckCircle2 size={11} /> confirmed
            </span>
          )}
        </div>
        <KV label="Name" value={name} />
        <KV label="Role" value={(me.role || "").replace(/_/g, " ")} />
        <KV label="Share %" value={me.share_percent ?? "—"} />
        <KV label="DOB" value={me.personal?.date_of_birth || "—"} />
        <KV label="PAN" value={me.identity?.pan || "—"} />
        <KV label="Aadhaar" value={me.identity?.aadhaar_last4 ? `•••• •••• ${me.identity.aadhaar_last4}` : "—"} mono />
        <KV label="Email" value={me.contact?.email || "—"} />
        <KV label="Mobile" value={me.contact?.mobile || "—"} />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Documents</div>
        <DocRow label="PAN card" ok={me.documents_uploaded?.pan_proof} />
        <DocRow label="Aadhaar card" ok={me.documents_uploaded?.aadhaar_proof} />
        <DocRow label="Photo" ok={me.documents_uploaded?.photo} />
      </div>
      <div className="text-[11px] text-slate-400 leading-relaxed">
        Infi cannot see other stakeholders, the company name, the registered office, or any pricing. Every action here is recorded in an audit log.
      </div>
    </div>
  );
}

function KV({ label, value, mono }) {
  return (
    <div className="flex justify-between py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={["text-slate-900 font-medium", mono ? "font-mono" : ""].join(" ")}>{value}</span>
    </div>
  );
}

function DocRow({ label, ok }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-slate-700">{label}</span>
      {ok
        ? <CheckCircle2 size={14} className="text-emerald-600" />
        : <span className="text-xs text-slate-400">not uploaded</span>}
    </div>
  );
}

function ExpiredOrRevoked({ status }) {
  const msg = status === "REVOKED"
    ? "This invitation has been revoked by the founder."
    : status === "EXPIRED"
      ? "This invitation has expired. Invitations are valid for 72 hours."
      : "We couldn't find this invitation.";
  return (
    <div className="min-h-screen bg-[var(--canvas)] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm text-center" data-testid="invite-expired">
        <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center mb-4 mx-auto">
          <AlertCircle size={20} />
        </div>
        <h1 className="font-display text-xl font-semibold mb-1">Link no longer valid</h1>
        <p className="text-sm text-slate-500 mb-4">{msg}</p>
        <p className="text-sm text-slate-600">Please ask your co-founder to send a new invite.</p>
      </div>
    </div>
  );
}
